/** @jsxImportSource @emotion/react */
import 'twin.macro';
import { css, keyframes } from '@emotion/react';
import { acknowledgeMaintenanceAlert } from 'components/VideoPlayer/Transcription/MediaUtilities';
import { useHistory } from 'react-router-dom';
import { Routes } from 'utils/routes';

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.75); }
`;

const badgeDotStyle = css`
  animation: ${pulse} 2s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const balancedText = css`
  text-wrap: balance;
`;

export const MaintenanceAlertPage = () => {
  const history = useHistory();

  const handleAccessLibrary = () => {
    acknowledgeMaintenanceAlert();
    history.replace(Routes.LIBRARY);
  };

  return (
    <div tw="height[calc(100vh - 70px)] overflow-y-auto w-full flex bg-white py-[48px] px-[16px]">
      <div tw="m-auto w-full max-w-[560px] flex flex-col items-center text-center">
        <div tw="inline-flex items-center gap-[7px] w-[fit-content] mb-[20px] rounded-full py-[5px] pl-[9px] pr-[12px] bg-sonnant-purple-2 bg-opacity-10 text-sonnant-purple-2 text-[12px] font-semibold uppercase letter-spacing[0.06em]">
          <span
            css={badgeDotStyle}
            tw="w-[7px] h-[7px] rounded-full bg-sonnant-purple-2"
          />
          Scheduled maintenance
        </div>

        <h1
          css={balancedText}
          tw="text-sonnant-blue-dark font-bold text-[28px] xs:text-[22px] line-height[1.25] letter-spacing[-0.02em] mb-[12px]"
        >
          We're upgrading your search experience
        </h1>

        <p
          css={balancedText}
          tw="text-sonnant-grey-5 text-[15px] line-height[1.65] mb-[32px] max-w-[42ch]"
        >
          Sonnant is undergoing a planned infrastructure upgrade to improve how
          you search and discover your content. Your library is available in
          read-only mode while we work.
        </p>

        <div tw="w-full flex flex-col gap-[10px] mb-[32px] text-left">
          <div tw="flex items-start gap-[12px] rounded-[12px] py-[14px] px-[16px] text-[14px] line-height[1.5] background[#ecfdf5] text-[#047857]">
            <span tw="flex-shrink-0 text-[16px] leading-none mt-[2px]">✓</span>
            <div>
              <div tw="font-semibold">Your library is accessible</div>
              <div tw="opacity-90">
                Browse, play, and review all existing content as normal.
              </div>
            </div>
          </div>
          <div tw="flex items-start gap-[12px] rounded-[12px] py-[14px] px-[16px] text-[14px] line-height[1.5] background[#fffbeb] text-[#92400e]">
            <span tw="flex-shrink-0 text-[16px] leading-none mt-[2px]">⏳</span>
            <div>
              <div tw="font-semibold">New uploads are safely queued</div>
              <div tw="opacity-90">
                Files you upload now are held in a secure queue and will be
                processed automatically once maintenance is complete — nothing
                will be lost.
              </div>
            </div>
          </div>
        </div>

        <hr tw="w-full m-0 border-0 border-t border-solid border-sonnant-grey-light mb-[24px]" />

        <div tw="flex flex-col items-center gap-[12px]">
          <button
            onClick={handleAccessLibrary}
            className="button btn-primary large"
            tw="(inline-flex items-center justify-center w-[unset] px-8 max-w-[unset] mb-0)!"
          >
            Access Library
          </button>
          <p tw="text-[13px] text-sonnant-grey-5 m-0">
            Edits and new uploads will resume automatically after maintenance.
            No action needed on your part.
          </p>
        </div>

        <div tw="w-full flex items-center justify-center gap-[8px] mt-[24px] pt-[20px] border-0 border-t border-solid border-sonnant-grey-light text-[13px] text-sonnant-grey-5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
            tw="flex-shrink-0 opacity-60"
          >
            <circle
              cx="7"
              cy="7"
              r="5.5"
              stroke="currentColor"
              strokeWidth="1.3"
            />
            <path
              d="M7 4.5V7l2 1.5"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </svg>
          Maintenance is in progress. We'll notify you once everything is back
          to full capacity.
        </div>
      </div>
    </div>
  );
};
